import classNames from "classnames"
import React from "react"

import CircledIconButton from "../../elements/V2/Buttons/CircledIconButton"
import Video from "../../elements/V2/Video"
import LayoutItem from "../Layouts/LayoutItem"

import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

export type BentoCardSize = "col-3" | "col-4" | "col-6" | "col-8" | "col-12"

type Props = {
  title: string
  description?: string
  image?: string
  videoUrl?: string
  imageAlt?: string
  backgroundColor?: Color
  imageFullHeight?: boolean
  imageAsBackground?: boolean
  showDescriptionOnHover?: boolean
  size: BentoCardSize
  ctaLink?: string
  mobileGridOrder?: number
  paymentStrip?: string
}

const BentoCard = ({
  description,
  title,
  image,
  videoUrl,
  backgroundColor = Color.Blue,
  imageAsBackground = false,
  imageFullHeight = false,
  showDescriptionOnHover = true,
  size,
  imageAlt,
  ctaLink,
  mobileGridOrder,
  paymentStrip,
  ...props
}: Props) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)

  const getCardColClass = (size: BentoCardSize) => {
    if (size === "col-3") {
      return "col-span-full flex-col sm:col-span-2 md:col-span-3"
    } else if (size === "col-6") {
      return "col-span-full flex-col sm:col-span-2 md:col-span-3 lg:col-span-6"
    } else if (size === "col-8") {
      return "col-span-full flex-col sm:col-span-2 sm:items-start md:col-span-4 md:flex-row lg:col-span-8"
    } else if (size === "col-12") {
      return "col-span-full flex-col sm:col-span-4 sm:flex-row sm:items-start md:col-span-6 lg:col-span-12"
    } else {
      // col-4 as default
      return "col-span-full flex-col sm:col-span-2 lg:col-span-4"
    }
  }

  const getImageColClass = (size: BentoCardSize) => {
    if (size === "col-3") {
      return "bottom-0 left-0 right-0 w-full min-[500px]:max-sm:px-[10%] sm:absolute lg:max-h-[68%]"
    } else if (size === "col-4" || size === "col-6") {
      return "bottom-0 left-0 right-0 w-full min-[500px]:max-sm:px-[10%] sm:absolute lg:max-h-[80%] min-[2000px]:px-[18%]"
    } else if (size === "col-12") {
      return "sm:absolute sm:bottom-0 sm:right-0 sm:w-[50%] md:w-[55%] lg:w-[50%]"
    } else {
      return [
        "",
        {
          "md:absolute md:bottom-0 md:right-0 md:top-0 md:w-[55%] md:self-end":
            !imageFullHeight,
        },
      ]
    }
  }

  const getHoverClass = (
    showDescriptionOnHover: boolean,
    size: BentoCardSize
  ) => {
    if (size === "col-3") {
      if (showDescriptionOnHover) {
        return "md:group-hover:-translate-y-20"
      }
      return "md:-translate-y-10"
    }

    if (showDescriptionOnHover) {
      return "md:group-hover:-translate-y-20"
    }
    return "md:-translate-y-10"
  }

  const cardClasses = classNames(
    "overflow-hidden group relative flex items-center justify-between rounded sm:items-start",
    !imageAsBackground && getBackgroundColorClass(backgroundColor),
    { "cursor-pointer": !!ctaLink },
    size === "col-3" ? "h-[389px] lg:h-[305px] 4k:h-[389px]" : "h-[389px]",
    getCardColClass(size)
  )

  const cardContent = (
    <>
      {image && imageAsBackground && (
        <div
          className={classNames(
            "absolute left-0 top-0 w-full",
            size === "col-3"
              ? "h-[389px] lg:h-[305px] 4k:h-[389px]"
              : "h-[389px]"
          )}
        >
          <GatsbyStoryblokImage
            image={image}
            alt={imageAlt ?? ""}
            loadType="eager"
            quality={80}
            aspectRatio="fullWidth"
            rounded
            className="h-full w-auto"
          />
        </div>
      )}
      {!!ctaLink && (
        <div
          className={classNames(
            "absolute bottom-32 left-32 z-10 -translate-y-10 transition duration-300 ease-in-out sm:translate-y-0"
          )}
        >
          <CircledIconButton
            color={backgroundColor === Color.Blue ? Color.White : Color.Blue}
            linkUrl={ctaLink}
            style="solid"
            enterOnGroupHover
          />
        </div>
      )}
      <div
        className={classNames(
          "relative z-10 flex flex-col gap-10 px-40 pt-[50px]",
          description &&
            "-translate-y-10 transition duration-300 ease-in-out sm:translate-y-0",
          getHoverClass(showDescriptionOnHover, size),
          (size === "col-8" || size === "col-12") && "md:max-w-[45%]"
        )}
      >
        <Typography
          text={title}
          size={size === "col-3" ? "lead-sm" : "lead-md"}
          font="grotesk"
          weight="medium"
          color={
            imageAsBackground ? Color.White : getAccentColor(backgroundColor)
          }
          className="max-w-[309px]"
        />
        {description && (
          <Typography
            text={description}
            size="body-sm"
            font="grotesk"
            weight="book"
            color={
              imageAsBackground ? Color.White : getAccentColor(backgroundColor)
            }
            className={classNames("max-w-[309px]", {
              "md:hidden md:group-hover:block": showDescriptionOnHover,
            })}
          />
        )}
        {paymentStrip && (
          <GatsbyStoryblokImage image={paymentStrip} alt="Payment strip" />
        )}
      </div>

      {image && !imageAsBackground && (
        <div
          className={classNames(
            "flex",
            imageFullHeight
              ? "mt-8 h-full max-w-full overflow-hidden md:mt-0"
              : "max-h-full max-w-full",
            getImageColClass(size)
          )}
        >
          {videoUrl ? (
            <Video
              video={videoUrl}
              loadType="eager"
              aspectRatio={imageFullHeight ? "fullWidth" : "4:3"}
              playOnMobile={true}
              coverImage={image}
              playbackMode="autoplay"
              ImageAlt={imageAlt ?? ""}
            />
          ) : (
            <GatsbyStoryblokImage
              image={image}
              alt={imageAlt ?? ""}
              className={classNames("pointer-events-none", {
                "h-full w-full": true,
              })}
              aspectRatio={imageFullHeight ? "auto" : "4:3"}
              imageObjectFit="cover"
            />
          )}
        </div>
      )}
    </>
  )

  if (ctaLink) {
    return (
      <LayoutItem
        mobileGridOrder={mobileGridOrder}
        as={Link}
        link={ctaLink}
        className={cardClasses}
        ref={animationRefCallback}
        {...props}
      >
        {cardContent}
      </LayoutItem>
    )
  }

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      className={cardClasses}
      ref={animationRefCallback}
      {...props}
    >
      {cardContent}
    </LayoutItem>
  )
}

export default BentoCard
